<template>
  <b-modal
    id="invoice-upload-modal"
    v-model="showModal"
    centered
    hide-footer
    dialog-class="invoice-upload-modal-dialog"
    content-class="invoice-upload-modal-content"
    header-class="invoice-upload-modal-header"
    body-class="invoice-upload-modal-body"
  >
    <!-- Header -->
    <template #modal-header="{ close }">
      <h5 class="modal-title w-100 text-center">Envio Nota Fiscal</h5>
      <b-button variant="link" class="close-btn p-0" @click="close">
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M12 4L4 12M4 4L12 12" stroke="#212529" stroke-opacity="0.5" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </b-button>
    </template>

    <div class="modal-body-container">
      <!-- Loading State -->
      <div v-if="isLoading" class="loading-container py-5 d-flex flex-column align-items-center justify-content-center">
        <b-spinner class="custom-spinner mb-3" label="Loading..."></b-spinner>
        <span class="loading-text">Aguarde, validando os arquivos...</span>
      </div>

      <!-- Default Form Content -->
      <div v-else class="form-container">
        <!-- Attention Warning Text -->
        <p class="warning-text mb-3">
          <strong>Atenção:</strong> soma do valor das Notas Fiscais anexadas deve ser igual ao valor indicado abaixo.
        </p>

        <!-- Production Info -->
        <div class="production-info mb-4">
          <div class="d-flex align-items-center mb-1">
            <i class="fas fa-plus-square modal-icon text-success mr-2"></i>
            <span class="production-label">{{ modalData.providerName || 'Produção Médica: Unimed Grande Florianópolis' }}</span>
          </div>
          <div class="value-nf-label">
            Valor NF: <strong class="text-secondary-value">R$ {{ modalData.invoiceValue || '47.493,11' }}</strong>
          </div>
        </div>

        <!-- File Upload Section with 32px Gap -->
        <div class="upload-sections-wrapper mb-4">
          
          <!-- XML Section Block -->
          <div class="upload-block">
            <div class="upload-btn-wrapper" @click="triggerFileInput('xml')">
              <input type="file" ref="xmlFileInput" accept=".xml" multiple class="d-none" @change="handleFileSelected($event, 'xml')" />
              <div class="upload-btn-inner">
                <i class="fas fa-upload upload-icon mr-2"></i>
                <span class="upload-btn-text">Anexar Notas Fiscais (Xml)</span>
              </div>
            </div>

            <!-- XML Attached Files List -->
            <div v-if="xmlFiles.length > 0" class="attached-files-list mt-2">
              <div v-for="(file, index) in xmlFiles" :key="'xml-' + index" class="file-item">
                <i class="fas fa-table file-icon mr-1"></i>
                <span class="file-name" :title="file.name">{{ file.name }}</span>
                <i class="fas fa-trash-alt remove-icon ml-1" @click.stop="removeFile('xml', index)"></i>
              </div>
            </div>
          </div>

          <!-- PDF Section Block -->
          <div class="upload-block">
            <div class="upload-btn-wrapper" @click="triggerFileInput('pdf')">
              <input type="file" ref="pdfFileInput" accept=".pdf" multiple class="d-none" @change="handleFileSelected($event, 'pdf')" />
              <div class="upload-btn-inner">
                <i class="fas fa-upload upload-icon mr-2"></i>
                <span class="upload-btn-text">Anexar Notas Fiscais (PDF)</span>
              </div>
            </div>

            <!-- PDF Attached Files List -->
            <div v-if="pdfFiles.length > 0" class="attached-files-list mt-2">
              <div v-for="(file, index) in pdfFiles" :key="'pdf-' + index" class="file-item">
                <i class="fas fa-file-pdf file-icon mr-1"></i>
                <span class="file-name" :title="file.name">{{ file.name }}</span>
                <i class="fas fa-trash-alt remove-icon ml-1" @click.stop="removeFile('pdf', index)"></i>
              </div>
            </div>
          </div>

        </div>

        <!-- Footer Actions -->
        <div class="modal-footer-actions d-flex justify-content-end gap-2 pt-3">
          <b-button variant="outline" class="btn-figma btn-cancel mr-2" @click="closeModal">
            Cancelar
          </b-button>
          <b-button class="btn-figma btn-submit" :disabled="!isSubmitEnabled" @click="submitInvoice">
            Enviar Nota Fiscal
          </b-button>
        </div>
      </div>
    </div>
  </b-modal>
</template>

<script>
export default {
  name: "InvoiceUploadModal",
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    modalData: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      xmlFiles: [],
      pdfFiles: [],
      isLoading: false,
    };
  },
  computed: {
    showModal: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", val);
      },
    },
    isSubmitEnabled() {
      return this.xmlFiles.length > 0 || this.pdfFiles.length > 0;
    },
  },
  methods: {
    triggerFileInput(type) {
      if (type === "xml") {
        this.$refs.xmlFileInput.click();
      } else if (type === "pdf") {
        this.$refs.pdfFileInput.click();
      }
    },
    handleFileSelected(event, type) {
      const selectedFiles = Array.from(event.target.files);
      if (type === "xml") {
        this.xmlFiles = [...this.xmlFiles, ...selectedFiles];
      } else if (type === "pdf") {
        this.pdfFiles = [...this.pdfFiles, ...selectedFiles];
      }
    },
    removeFile(type, index) {
      if (type === "xml") {
        this.xmlFiles.splice(index, 1);
        if (this.xmlFiles.length === 0 && this.$refs.xmlFileInput) {
          this.$refs.xmlFileInput.value = "";
        }
      } else if (type === "pdf") {
        this.pdfFiles.splice(index, 1);
        if (this.pdfFiles.length === 0 && this.$refs.pdfFileInput) {
          this.$refs.pdfFileInput.value = "";
        }
      }
    },
    closeModal() {
      this.showModal = false;
    },
    submitInvoice() {
      this.isLoading = true;
      setTimeout(() => {
        this.$emit("upload-success", {
          xmlFiles: this.xmlFiles,
          pdfFiles: this.pdfFiles,
          data: this.modalData,
        });
        this.isLoading = false;
        this.closeModal();
      }, 2000);
    },
  },
};
</script>

<style lang="scss">
.invoice-upload-modal-dialog {
  max-width: 800px !important;
  width: 800px;

  @media (max-width: 768px) {
    max-width: 95vw !important;
    width: 95vw !important;
    margin: 10px auto !important;
  }
}

.invoice-upload-modal-content {
  border: 1px solid #CED4DA !important;
  border-radius: 4px !important;
  background-color: #FFFFFF !important;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
}

.invoice-upload-modal-header {
  height: 64px;
  padding: 20px 32px !important;
  border-bottom: none !important;
  background-color: #FFFFFF !important;
  display: flex !important;
  align-items: center !important;

  @media (max-width: 768px) {
    padding: 16px !important;
    height: 52px;
  }

  .modal-title {
    font-family: 'Trebuchet MS', sans-serif !important;
    font-style: normal !important;
    font-weight: 400 !important;
    font-size: 20px !important;
    line-height: 24px !important;
    color: #212529 !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    text-align: center !important;
    margin: 0 !important;
  }

  .close-btn {
    background: transparent !important;
    border: none !important;
    box-shadow: none !important;
    padding: 0 !important;
  }
}

.invoice-upload-modal-body {
  background-color: #FFFFFF !important;
  padding: 0 32px 32px 32px !important;

  @media (max-width: 768px) {
    padding: 0 16px 20px 16px !important;
  }
}
</style>

<style lang="scss" scoped>
.modal-body-container {
  font-family: 'Trebuchet MS', sans-serif;
  background-color: #FFFFFF;

  .warning-text {
    font-size: 12.8px;
    line-height: 19px;
    color: #013619;
  }

  .production-info {
    .modal-icon {
      font-size: 20px;
      color: #007A4A !important;
    }

    .production-label {
      font-size: 14px;
      color: #232428;
    }

    .value-nf-label {
      font-size: 16px;
      color: #212529;

      .text-secondary-value {
        color: #00423E;
        font-size: 16px;
        font-weight: 700;
      }
    }
  }

  .upload-sections-wrapper {
    display: flex;
    flex-direction: column;
    gap: 32px;

    .upload-block {
      display: flex;
      flex-direction: column;

      .upload-btn-wrapper {
        width: 100%;
        height: 36px;
        background: #F4F6F6;
        border-radius: 4px;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 6px 16px;
        transition: background 0.2s ease;

        &:hover {
          background: #e2e8e8;
        }

        .upload-btn-inner {
          display: flex;
          align-items: center;
          justify-content: center;

          .upload-icon {
            font-size: 18px;
            color: #013619;
          }

          .upload-btn-text {
            font-size: 16px;
            line-height: 24px;
            color: #013619;
          }
        }
      }
      
      .attached-files-list {
        display: flex;
        flex-wrap: wrap;
        gap: 16px;

        .file-item {
          display: flex;
          align-items: center;
          font-size: 14px;
          color: #212529;

          .file-icon {
            font-size: 18px;
            color: #393C40;
          }

          .file-name {
            max-width: 180px;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          }

          .remove-icon {
            cursor: pointer;
            color: #007A4A;
            font-size: 16px;

            &:hover {
              color: #b22222;
            }
          }
        }
      }
    }
  }

  .modal-footer-actions {
    border-top: 1px solid #DEE2E6;

    .btn-figma {
      height: 36px;
      padding: 6px 20px;
      font-size: 16px;
      font-family: 'Trebuchet MS', sans-serif;
      border-radius: 4px;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .btn-cancel {
      border: 1px solid #007A4A;
      color: #007A4A;
      background: #FFFFFF;

      &:hover {
        background: #f0f8f4;
      }
    }

    .btn-submit {
      background: #007A4A;
      border: 1px solid #007A4A;
      color: #FFFFFF;

      &:hover:not(:disabled) {
        background: #00633b;
      }

      &:disabled {
        background: #80bd9e;
        border-color: #80bd9e;
      }
    }
  }

  .loading-container {
    .custom-spinner {
      width: 3.5rem;
      height: 3.5rem;
      color: #007A4A;
      border-width: 0.35em;
    }

    .loading-text {
      font-size: 14px;
      color: #393C40;
    }
  }
}
</style>